import { useLayoutEffect, useRef, useState } from "react";
import CommonTextArea from "./CommonTextArea";

// About five lines; longer queries scroll inside the box.
const MAX_HEIGHT_PX = 112;

/**
 * One-line search query that opens up while editing, so a long query is readable and editable
 * without scrolling sideways. Shown as a single truncated line until clicked (when
 * `collapsible`); while editing it grows with the text. Enter searches, Escape stops editing.
 *
 * @param {{
 *   value: string;
 *   onChange: (value: string) => void;
 *   onSubmit?: (value: string) => void;
 *   onBlur?: () => void;
 *   placeholder?: string;
 *   ariaLabel?: string;
 *   autoFocus?: boolean;
 *   collapsible?: boolean;
 *   textClassName?: string;
 * }} props
 */
export default function QueryTextInput({
  value,
  onChange,
  onSubmit,
  onBlur,
  placeholder = "",
  ariaLabel,
  autoFocus = false,
  collapsible = false,
  textClassName = "text-gray-800 dark:text-gray-100",
}) {
  const ref = useRef(null);
  // Collapsed whenever it is not being edited, including when a query arrives from elsewhere.
  const [editing, setEditing] = useState(false);
  const showInput = !collapsible || editing || !value;

  // Grow with the text, up to the cap.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT_PX)}px`;
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT_PX ? "auto" : "hidden";
  }, [value, showInput]);

  // Opened by a click: focus with the caret at the end, where edits usually happen.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !showInput || !(autoFocus || editing)) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when editing starts
  }, [showInput]);

  if (!showInput) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        title={value}
        aria-label={ariaLabel ? `${ariaLabel}: ${value}` : value}
        className={`min-w-0 flex-1 truncate text-left text-sm font-normal cursor-text ${textClassName}`}
      >
        {value}
      </button>
    );
  }

  return (
    <CommonTextArea
      ref={ref}
      rows={1}
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      // A query is one sentence: pasted line breaks become spaces.
      onChange={(event) => onChange(event.target.value.replace(/\s*\n\s*/g, " "))}
      onKeyDown={(event) => {
        if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
          event.preventDefault();
          onSubmit?.(value);
        }
        if (event.key === "Escape") event.currentTarget.blur();
      }}
      onFocus={() => setEditing(true)}
      onBlur={() => {
        setEditing(false);
        onBlur?.();
      }}
      className="!min-w-0 flex-1 !border-0 !shadow-none !bg-transparent focus-within:!border-0"
      textAreaClassName={`!px-0 !py-1.5 !text-sm leading-5 placeholder:!text-gray-400 ${textClassName}`}
    />
  );
}
